import { NavigationExtras } from "@angular/router";
import { ActionCommandResponseDto } from "@app/core/dto/action-command-response.dto";
import { RequestDataDto } from "@app/core/dto/request-data.dto";
import { QUERY_PARAMS_ACTION_TYPES } from "@app/core/enums/query-params-action-types.enum";
import { ActionCommand } from "../action-command-interface";

export class MergeQueryParamsCommand implements ActionCommand {
  constructor(private renderEngineComponent: any) { }

  async execute(actionData: any, requestData: RequestDataDto): Promise<ActionCommandResponseDto> {
    this.handleQueryParamsAction(actionData);
    return;
  }

  handleQueryParamsAction(actionData: any) {
    let queryParams = { ...this.renderEngineComponent.activatedRoute.snapshot.queryParams };

    // Determine if we are applying a filter or any action that should reset the page
    if (queryParams?.page && !actionData?.isPaginationAction) {
      queryParams.page = null;
    }

    switch (actionData.params.type) {
      case QUERY_PARAMS_ACTION_TYPES.overwrite: {
        for (const paramName in queryParams || {}) {
          queryParams[paramName] = null;
        }

        queryParams = {
          ...queryParams,
          ...(actionData?.params?.value || {}),
        };
        break;
      }
      case QUERY_PARAMS_ACTION_TYPES.preserve: {
        queryParams = {
          ...(actionData?.params?.value || {}),
          ...queryParams,
        };
        break;
      }
      /* The above TypeScript code snippet is handling an action of type
      `QUERY_PARAMS_ACTION_TYPES.update`. It is updating the query parameters based on the `params`
      value provided in the action data. */
      case QUERY_PARAMS_ACTION_TYPES.update: {
        const paramsToUpdate = actionData?.params?.value || {};
        for (const paramName in paramsToUpdate) {
          const newValues = [].concat(paramsToUpdate[paramName]);
          const existingValues = queryParams[paramName]
            ? [].concat(queryParams[paramName])
            : [];
          const uniqueValues = {};

          existingValues.concat(newValues).forEach((value) => {
            uniqueValues[value] = true;
          });

          queryParams[paramName] = Object.keys(uniqueValues);
        }
        break;
      }

      /* The above code is a TypeScript switch case statement handling the action type
      QUERY_PARAMS_ACTION_TYPES.remove. It retrieves the parameters to remove from the action data,
      loops through each parameter, and removes the specified value from the existing values in the
      queryParams object. It then updates the queryParams object with the filtered values, handling
      cases where there are multiple values left for a parameter. */
      case QUERY_PARAMS_ACTION_TYPES.remove: {
        const paramsToRemove = actionData?.params?.value || {};
        for (const paramName in paramsToRemove) {
          const paramValueToRemove = paramsToRemove[paramName];
          const existingValues = queryParams[paramName]
            ? [].concat(queryParams[paramName])
            : [];
          const filteredValues = existingValues.filter(
            (value) => value !== paramValueToRemove,
          );

          queryParams[paramName] = filteredValues.length
            ? filteredValues.length === 1
              ? filteredValues[0]
              : filteredValues
            : undefined;
        }
        break;
      }

      // default merge
      case QUERY_PARAMS_ACTION_TYPES.merge:
      default: {
        queryParams = {
          ...queryParams,
          ...(actionData?.params?.value || {}),
        };
        break;
      }
    }
    const navigationExtras: NavigationExtras = {
      relativeTo: this.renderEngineComponent.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: false,
    };
    this.renderEngineComponent.navigateTo([], navigationExtras);
  }
}